import {
  ActivatedRouteSnapshot,
  BaseRouteReuseStrategy,
  DetachedRouteHandle
} from '@angular/router';
import { AuthService } from '@core/services';
import { Injectable, inject } from '@angular/core';

/*
 * This is a custom RouteReuseStrategy that will reuse the same component instance.
 * The major use case for this is to allow a user to be on an entity detail/edit page from
 * a list page and then navigate back without the list regenerating.
 * This is a very common use case. It has edge cases for concurrency and other issues
 * so the functionality should only occur when navigating directly from one to the other.
 * 1. shouldDetach - only detach if the route is a list page
 * 2. store - store the route handle that was detached
 * 3. shouldAttach - only attach if the route is a list page previously detached, and stored.
 * 4. retrieve - retrieve the stored route handle which is the component instance with its original state.
 * 5. shouldReuseRoute - using the base strategy, reuse the route if the route config is the same.
 */
/** Whether two snapshots of the same route config address the same record. */
function sameParams(
  future: ActivatedRouteSnapshot,
  curr: ActivatedRouteSnapshot
): boolean {
  const futureKeys = Object.keys(future.params);
  const currKeys = Object.keys(curr.params);
  return (
    futureKeys.length === currKeys.length &&
    futureKeys.every((key) => future.params[key] === curr.params[key])
  );
}

@Injectable()
export class NymblRouteReuseStrategy extends BaseRouteReuseStrategy {
  private authService = inject(AuthService);

  private detachedHandles: Map<string, DetachedRouteHandle> = new Map();

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    if (route.data?.listView) {
      return true;
    }
    return super.shouldDetach(route);
  }

  store(
    route: ActivatedRouteSnapshot,
    detachedTree: DetachedRouteHandle
  ): void {
    // clear out detachedHandles if we're no longer logged in
    if (this.authService.isLoggedIn$.value) {
      const key = this.getRouteKey(route);
      this.detachedHandles.set(key, detachedTree);
    } else {
      this.detachedHandles.clear();
    }
  }

  /**
   * Routes are stored as an array of route configs, so we can find any with url property and join them to create the URL for the route
   * @param route
   * @private
   * <br> <p>
   * [Tutorial Source](https://www.intertech.com/angular-development-10-routereusestrategy-maintaining-component-state/)
   */
  private getRouteKey(route: ActivatedRouteSnapshot): string {
    return route.pathFromRoot
      .filter((u) => u.url)
      .map((u) => u.url)
      .join('/');
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    return this.detachedHandles.has(this.getRouteKey(route));
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    return this.detachedHandles.get(this.getRouteKey(route));
  }

  /**
   * Reuses the component for a matching route config, as the base strategy does, unless the
   * route opts out with `data: { reuseComponent: false }`.
   *
   * Opting out matters for a detail form reached as `.../:id`. The default is to keep one
   * component instance while only the params change, so `ngOnInit` never runs again and the
   * form has to notice the new record itself — which means watching the resolved input and
   * re-filling the form, and that same watch will happily overwrite a half-typed record when
   * the router re-supplies the input. Destroying the component on an id change removes the
   * question: each record gets a fresh form, filled once on init.
   *
   * Identical params still reuse, so a re-activation of the same URL (a query param change,
   * a resolver re-run) does not throw away a form the user is in the middle of.
   */
  shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot
  ): boolean {
    if (
      super.shouldReuseRoute(future, curr) &&
      future.routeConfig?.data?.['reuseComponent'] === false
    ) {
      return sameParams(future, curr);
    }
    return super.shouldReuseRoute(future, curr);
  }
}
